import React, { useEffect, useState, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { ExpenseService } from "services/expense.service";
import { IExpenseResponse } from "models/church.types";

export default function ExpenseList() {
  const [expenses, setExpenses] = useState<IExpenseResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const history = useHistory();

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Calcul du total des dépenses affichées (Page actuelle)
  const totalAmount = expenses.reduce(
    (acc, curr) => acc + (curr.amount || 0),
    0
  );

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      let result;
      if (query.trim()) {
        result = await ExpenseService.search(query, page, itemsPerPage);
      } else {
        result = await ExpenseService.getAll(page, itemsPerPage);
      }

      setExpenses(result.data || []);
      setTotalItems(result.totalCount || 0);
    } catch (error) {
      console.error("Erreur chargement dépenses:", error);
      toast.error("Impossible de charger la liste des dépenses");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    []
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Supprimer cette dépense ?")) {
      setLoading(true);
      try {
        await ExpenseService.delete(id);
        toast.success("Dépense supprimée avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error("Erreur lors de la suppression.");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(amount);
  };

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* TITRE PRINCIPAL */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Gestion des Dépenses
        </h2>
        <p className="text-slate-400 text-sm italic">
          Suivi des sorties de fonds et dépenses opérationnelles par caisse
        </p>
      </div>

      {/* BARRE D'OUTILS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="relative w-full md:w-96 group">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
            {loading ? (
              <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
            ) : (
              <i className="fas fa-search"></i>
            )}
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher par titre..."
            className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        <Link
          to="/admin/expenses/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-plus mr-2 text-base"></i> Nouvelle dépense
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Date</th>
                <th className="px-6 py-5">Dépense</th>
                <th className="px-6 py-5">Description</th>
                <th className="px-6 py-5">Catégorie</th>
                <th className="px-6 py-5">Caisse</th>
                <th className="px-6 py-5 text-right">Montant</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {expenses.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-wallet block text-3xl mb-3 opacity-20"></i>
                    Aucune dépense trouvée.
                  </td>
                </tr>
              ) : (
                expenses.map((exp) => (
                  <tr
                    key={exp.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    <td className="px-6 py-4 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(exp.expenseDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-white uppercase">
                        {exp.title}
                      </div>
                      {exp.approvedByLogin && (
                        <div className="text-[9px] text-[#D4AF37] mt-1 italic">
                          Approuvé par : {exp.approvedByLogin}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400 max-w-[200px] truncate">
                      {exp.description || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 rounded-full bg-slate-900 text-[#D4AF37] text-[10px] font-black uppercase border border-[#D4AF37]/20">
                        {exp.category ?? "Général"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {exp.fundName ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-[#D4AF37] border border-slate-700 shadow-sm">
                          <i className="fas fa-wallet mr-1.5 text-[10px]"></i>
                          {exp.fundName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-400 border border-slate-600/40">
                          <i className="fas fa-exclamation-circle mr-1.5 text-[10px] text-amber-500/70"></i>
                          Non rattachée
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <span className="text-sm font-black text-red-400">
                        {formatCurrency(exp.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-5">
                        <Link
                          to={`/admin/expenses/edit/${exp.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(exp.id!)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-[#1E293B]">
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-right font-black text-slate-300 uppercase text-[10px]"
                >
                  Total Page :
                </td>
                <td className="px-6 py-4 text-right font-black text-[#D4AF37]">
                  {formatCurrency(totalAmount)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total global : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            dépenses
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}